import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useEffect, useRef } from "react";
import { setLocationsData, setZoneAndWardData, getLocationSummary, setLocationSummaryData } from "../Redux/locations/action";
import { setErrorStatus } from "../Redux/status/action";
import { database } from "../Services/firebase";

function getUniqueValues(key, obj) {
  if (!obj) return [];
  return [...new Set(Object.values(obj).map((item) => item[key]).filter(Boolean))];
}

function getComplaintsCount(item, type) {
  const complaintCount = item.complaintCount;
  if (!complaintCount) return 0;
  let total = 0;
  for (const dateKey of Object.keys(complaintCount)) {
    const dayData = complaintCount[dateKey];
    if (dayData && dayData[type]) {
      total += dayData[type];
    }
  }
  return total;
}

function getCompletedProcedureCount(item) {
  const procedureCount = item.procedureCount;
  if (!procedureCount) return 0;
  let total = 0;
  for (const year of Object.values(procedureCount)) {
    for (const month of Object.values(year)) {
      for (const day of Object.values(month)) {
        total += (day.positive || 0) + (day.negative || 0);
      }
    }
  }
  return total;
}

function getStatusCount(item) {
  return getComplaintsCount(item, "open") - getComplaintsCount(item, "closed");
}

function getValidStatusByCount(count) {
  if (count > 5) return "bad";
  if (count > 2) return "warn";
  return "ok";
}

function calculateLocationData(locations) {
  let updatedData = {};
  let manPowerPercentageArray = [];
  let lastManpowerCapturedArray = [];

  for (const [key, item] of Object.entries(locations)) {
    const { complaintCount, procedureCount } = item;

    const totalOpenCompliant = complaintCount ? getComplaintsCount(item, "open") : 0;
    const totalClosedCompliant = complaintCount ? getComplaintsCount(item, "closed") : 0;
    const totalUnderReviewCompliant = complaintCount ? getComplaintsCount(item, "underReview") : 0;
    const totalCompletedProcedure = procedureCount ? getCompletedProcedureCount(item) : 0;
    const status = getValidStatusByCount(getStatusCount(item));

    manPowerPercentageArray.push("N/A");
    lastManpowerCapturedArray.push(0);

    updatedData[key] = {
      ...item,
      totalManPower: "N/A",
      totalManPowerCapacity: "N/A",
      totalOpenCompliant,
      totalClosedCompliant,
      totalUnderReviewCompliant,
      totalCompletedProcedure,
      status,
      operationalStatusState: item?.operationalStatus?.state
    };
  }

  setLocationSummaryData("0.00", "0.00");
  return updatedData;
}

export default function useLocations({ isAuth, allowedZones }) {
  const locationsListener = useRef(null);

  useEffect(() => {
    if (isAuth && locationsListener.current === null) {
      locationsListener.current = subscribeToLocations(allowedZones);
    } else if (isAuth === false && typeof locationsListener.current === "function") {
      locationsListener.current();
      locationsListener.current = null;
    }
    // eslint-disable-next-line
  }, [isAuth]);
}

function subscribeToLocations(allowedZones) {
  const collectionRef = collection(database, "locations");
  // Firestore caps an "in" clause at 30 values.
  const q = Array.isArray(allowedZones) && allowedZones.length > 0
    ? query(collectionRef, where("zone", "in", allowedZones.slice(0, 30)))
    : collectionRef;
  let locations = {};

  return onSnapshot(
    q,
    (dataSnapshot) => {
      locations = {};
      dataSnapshot.forEach((doc) => {
        if (!doc.data().actions?.deletedAt) {
          locations[doc.id] = { ...doc.data(), documentId: doc.id };
        }
      });

      const updatedData = calculateLocationData(locations);
      setLocationsData(updatedData);

      setZoneAndWardData({
        zones: getUniqueValues("zone", locations).sort((a, b) => a - b),
        wards: getUniqueValues("ward", locations).sort((a, b) =>
          String(a).localeCompare(String(b))
        )
      });

      getLocationSummary();
    },
    (error) => {
      console.error(error, "from locations listener");
      setErrorStatus(error);
    }
  );
}
